import { useState, useEffect, Fragment } from 'react'
import { useConfirm } from 'popup/ConfirmContext'
import { StudentCreate, StudentUpdate, StudentDelete } from "services/studentService";
import { Dialog, Transition } from '@headlessui/react'
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ErrorModal from "popup/ErrorModal"
import ConfirmDialog from "popup/ConfirmDialog"
import {toast} from "react-hot-toast";

interface StudentModalProps {
    isOpen: boolean
    onClose: () => void
    courseId: string
    student?: any
    refreshData: () => void
}

export default function SaveStudentModal({ isOpen, onClose, courseId, student, refreshData }: StudentModalProps) {
    const [formData, setFormData] = useState({
        fullName: '',
        profileNumber: '',
        identifyNumber: '',
        identifyDate: null as Date | null,
        identifyPlace: '',
        registerCode: '',
        birthDate: null as Date | null,
        gender: '1',
        phoneNumber: '',
        placeOfBirth: '',
        hometown: '',
        permanentAddress: '',
        ethnicity: '',
        educationLevel: '',
        nationality: 'Việt Nam',
        licenseClass: '',
        licenseDate: null as Date | null,
        licenseNumber: '',
        licensePlace: '',
        licenseReceiveDate: null as Date | null
    })
    const [isEditMode, setIsEditMode] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [errorModalOpen, setErrorModalOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const { confirm } = useConfirm()

    useEffect(() => {
        if (student) {
            setIsEditMode(true)
            setFormData({
                fullName: student.fullName || '',
                profileNumber: student.profileNumber || '',
                identifyNumber: student.identifyNumber || '',
                identifyDate: student.identifyDate ? new Date(student.identifyDate) : null,
                identifyPlace: student.identifyPlace || '',
                registerCode: student.registerCode || '',
                birthDate: student.birthDate ? new Date(student.birthDate) : null,
                gender: student.gender?.toString() || '1',
                phoneNumber: student.phoneNumber || '',
                placeOfBirth: student.placeOfBirth || '',
                hometown: student.hometown || '',
                permanentAddress: student.permanentAddress || '',
                ethnicity: student.ethnicity || '',
                educationLevel: student.educationLevel || '',
                nationality: student.nationality || 'Việt Nam',
                licenseClass: student.licenseClass || '',
                licenseDate: student.licenseDate ? new Date(student.licenseDate) : null,
                licenseNumber: student.licenseNumber || '',
                licensePlace: student.licensePlace || '',
                licenseReceiveDate: student.licenseReceiveDate ? new Date(student.licenseReceiveDate) : null
            })
        } else {
            setIsEditMode(false)
            setFormData({
                fullName: '',
                profileNumber: '',
                identifyNumber: '',
                identifyDate: null,
                identifyPlace: '',
                registerCode: '',
                birthDate: null,
                gender: '1',
                phoneNumber: '',
                placeOfBirth: '',
                hometown: '',
                permanentAddress: '',
                ethnicity: '',
                educationLevel: '',
                nationality: 'Việt Nam',
                licenseClass: '',
                licenseDate: null,
                licenseNumber: '',
                licensePlace: '',
                licenseReceiveDate: null
            })
        }
    }, [student])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleDateChange = (name: 'identifyDate' | 'birthDate' | 'licenseDate' | 'licenseReceiveDate') => (date: Date | null) => {
        setFormData(prev => ({
            ...prev,
            [name]: date
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        try {
            const payload = {
                ...formData,
                courseId,
                gender: parseInt(formData.gender, 10),
                birthDate: formData.birthDate ? formData.birthDate.toISOString() : null
            }

            const apiCall = isEditMode
                ? await StudentUpdate({ ...payload, id: student.id })
                : await StudentCreate(payload)

            toast.success(isEditMode ? 'Cập nhật học viên thành công' : 'Thêm học viên mới thành công')

            refreshData()
            onClose()
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || 'Đã xảy ra lỗi không xác định'
            setErrorMessage(errorMsg)
            setErrorModalOpen(true)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async () => {
        setShowDeleteConfirm(true)
    }

    const confirmDelete = async () => {
        setIsLoading(true)
        try {
            await StudentDelete(student.id)
            toast.success('Xóa học viên thành công')
            refreshData()
            onClose()
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || 'Xóa học viên thất bại'
            setErrorMessage(errorMsg)
            setErrorModalOpen(true)
        } finally {
            setIsLoading(false)
            setShowDeleteConfirm(false)
        }
    }

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900"
                                    >
                                        {isEditMode ? 'Cập nhật học viên' : 'Thêm học viên mới'}
                                    </Dialog.Title>

                                    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Mã đăng ký</label>
                                                <input
                                                    type="text"
                                                    name="registerCode"
                                                    value={formData.registerCode}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Số hồ sơ</label>
                                                <input
                                                    type="text"
                                                    name="profileNumber"
                                                    value={formData.profileNumber}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Họ và tên *</label>
                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    value={formData.fullName}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    required
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Số CCCD/HC *</label>
                                                <input
                                                    type="text"
                                                    name="identifyNumber"
                                                    value={formData.identifyNumber}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    required
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Nơi cấp CCCD/HC</label>
                                                <input
                                                    type="text"
                                                    name="identifyPlace"
                                                    value={formData.identifyPlace}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Ngày cấp CCCD/HC</label>
                                                <DatePicker
                                                    selected={formData.identifyDate}
                                                    // @ts-ignore
                                                    onChange={handleDateChange('identifyDate')}
                                                    dateFormat="dd/MM/yyyy"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                    placeholderText="Chọn ngày cấp CCCD/HC"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Ngày sinh</label>
                                                <DatePicker
                                                    selected={formData.birthDate}
                                                    // @ts-ignore
                                                    onChange={handleDateChange('birthDate')}
                                                    dateFormat="dd/MM/yyyy"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                    placeholderText="Chọn ngày sinh"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Giới tính</label>
                                                <select
                                                    name="gender"
                                                    value={formData.gender}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                >
                                                    <option value="1">Nam</option>
                                                    <option value="2">Nữ</option>
                                                    <option value="3">Khác</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Số điện thoại</label>
                                                <input
                                                    type="text"
                                                    name="phoneNumber"
                                                    value={formData.phoneNumber}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            {/*<div>*/}
                                            {/*    <label className="block text-sm font-medium text-gray-700 mb-1">Nơi sinh</label>*/}
                                            {/*    <input*/}
                                            {/*        type="text"*/}
                                            {/*        name="placeOfBirth"*/}
                                            {/*        value={formData.placeOfBirth}*/}
                                            {/*        onChange={handleChange}*/}
                                            {/*        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"*/}
                                            {/*        disabled={isLoading}*/}
                                            {/*    />*/}
                                            {/*</div>*/}

                                            {/*<div>*/}
                                            {/*    <label className="block text-sm font-medium text-gray-700 mb-1">Quê quán</label>*/}
                                            {/*    <input*/}
                                            {/*        type="text"*/}
                                            {/*        name="hometown"*/}
                                            {/*        value={formData.hometown}*/}
                                            {/*        onChange={handleChange}*/}
                                            {/*        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"*/}
                                            {/*        disabled={isLoading}*/}
                                            {/*    />*/}
                                            {/*</div>*/}

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Địa chỉ thường trú</label>
                                                <input
                                                    type="text"
                                                    name="permanentAddress"
                                                    value={formData.permanentAddress}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            {/*<div>*/}
                                            {/*    <label className="block text-sm font-medium text-gray-700 mb-1">Dân tộc</label>*/}
                                            {/*    <input*/}
                                            {/*        type="text"*/}
                                            {/*        name="ethnicity"*/}
                                            {/*        value={formData.ethnicity}*/}
                                            {/*        onChange={handleChange}*/}
                                            {/*        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"*/}
                                            {/*        disabled={isLoading}*/}
                                            {/*    />*/}
                                            {/*</div>*/}

                                            {/*<div>*/}
                                            {/*    <label className="block text-sm font-medium text-gray-700 mb-1">Trình độ văn hóa</label>*/}
                                            {/*    <input*/}
                                            {/*        type="text"*/}
                                            {/*        name="educationLevel"*/}
                                            {/*        value={formData.educationLevel}*/}
                                            {/*        onChange={handleChange}*/}
                                            {/*        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"*/}
                                            {/*        disabled={isLoading}*/}
                                            {/*    />*/}
                                            {/*</div>*/}

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Quốc tịch</label>
                                                <input
                                                    type="text"
                                                    name="nationality"
                                                    value={formData.nationality}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Hạng GPLX đã có</label>
                                                <input
                                                    type="text"
                                                    name="licenseName"
                                                    value={formData.licenseClass}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Số GPLX đã có</label>
                                                <input
                                                    type="text"
                                                    name="licenseNumber"
                                                    value={formData.licenseNumber}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Nơi cấp GPLX</label>
                                                <input
                                                    type="text"
                                                    name="licensePlace"
                                                    value={formData.licensePlace}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Ngày cấp GPLX</label>
                                                <DatePicker
                                                    selected={formData.licenseDate}
                                                    onChange={handleDateChange('licenseDate')}
                                                    dateFormat="dd/MM/yyyy"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                    placeholderText="Chọn ngày cấp GPLX"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Ngày nhận Hồ sơ</label>
                                                <DatePicker
                                                    selected={formData.licenseReceiveDate}
                                                    onChange={handleDateChange('licenseReceiveDate')}
                                                    dateFormat="dd/MM/yyyy"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    disabled={isLoading}
                                                    placeholderText="Chọn ngày sinh"
                                                />
                                            </div>

                                        </div>

                                        <div className="flex justify-between pt-4">
                                            {isEditMode && (
                                                <button
                                                    type="button"
                                                    onClick={handleDelete}
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Xóa
                                                </button>
                                            )}
                                            <div></div>
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={onClose}
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                                                >
                                                    Hủy
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isLoading ? (
                                                        <span className="flex items-center">
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            {isEditMode ? 'Đang cập nhật...' : 'Đang thêm...'}
                                                        </span>
                                                    ) : isEditMode ? 'Cập nhật' : 'Thêm mới'}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Xác nhận xóa học viên"
                description="Bạn có chắc chắn muốn xóa học viên này?"
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
            />

            <ErrorModal
                isOpen={errorModalOpen}
                message={errorMessage}
                onClose={() => setErrorModalOpen(false)}
            />
        </>
    )
}